<svelte:options immutable />

<script lang="ts">
import type { ClassValue } from 'svelte/elements';

import { Button, IconButton } from '../button';
import { Icon } from '../icon';
import { Progress } from '../progress';
import {
  DisplayDetailsByVariant,
  ToastVariant,
  type ToastVariantType,
} from './variants';

/** The message displayed on the toast */
export let message: string;
/** Function called when the close button is clicked */
export let dismiss: () => void;
/** The action performed on the button */
export let action: { text: string; handler: () => unknown } | undefined =
  undefined;

/** The severity of the notification you want to show users*/
export let variant: ToastVariantType = ToastVariant.Success;

/**
 * Optional secondary line shown below the message in smaller text.
 * When set, the toast switches to a taller layout.
 */
export let subMessage: string | undefined = undefined;

/**
 * Progress value between 0 and 100. When provided, the toast shows a
 * progress bar at the bottom and replaces the variant icon with a loading
 * spinner while in progress and a success icon when complete.
 */
export let percent: number | undefined = undefined;

/** Color of the progress bar. Defaults to green. */
export let progressColor = 'bg-success-dark';

$: displayDetails = DisplayDetailsByVariant[variant];
$: clampedPercent =
  percent === undefined ? undefined : Math.min(100, Math.max(0, percent));
$: isProgressMode = percent !== undefined || variant === ToastVariant.Progress;
$: isComplete = clampedPercent === 100;

/** Additional CSS classes to pass to the banner. */
let extraClasses: ClassValue = '';
export { extraClasses as cx };
</script>

<div
  class={[
    'border-medium bg-medium text-default relative flex w-max max-w-[480px] items-center border pr-1 pl-3 text-sm shadow-sm',
    isProgressMode ? 'min-w-[300px]' : '',
    subMessage ? 'min-h-12 py-2' : 'h-10',
    extraClasses,
  ]}
>
  <div class="mr-4 flex min-w-0 flex-1 gap-4 truncate">
    {#if isProgressMode}
      {#if isComplete}
        <Icon
          size="lg"
          name="check-circle"
          cx={['shrink-0', 'text-success-dark']}
          role="img"
          aria-hidden={false}
          aria-label="success"
        />
      {:else}
        <div
          class="shrink-0"
          role="img"
          aria-label="loading"
        >
          <Progress size="small" />
        </div>
      {/if}
    {:else}
      <Icon
        size="lg"
        name={displayDetails.icon}
        cx={['shrink-0', displayDetails.iconClasses]}
        role="img"
        aria-hidden={false}
        aria-label={displayDetails.label}
      />
    {/if}
    <div class="min-w-0">
      <p class="truncate">{message}</p>
      {#if subMessage}
        <p class="text-subtle-1 truncate text-xs">{subMessage}</p>
      {/if}
    </div>
  </div>

  <div class="flex">
    {#if action}
      <Button
        height="fixed"
        textSize="text-sm"
        variant="ghost"
        type="button"
        onclick={action.handler}
      >
        {action.text}
      </Button>
    {/if}
    <IconButton
      variant="ghost"
      cx="text-gray-7"
      label="Dismiss toast"
      icon="close"
      on:click={dismiss}
    />
  </div>

  {#if clampedPercent !== undefined}
    <div
      class={[
        'absolute bottom-0 left-0 h-[4px] transition-all duration-300',
        progressColor,
      ]}
      style="width: {clampedPercent}%"
      role="progressbar"
      aria-valuenow={clampedPercent}
      aria-valuemin={0}
      aria-valuemax={100}
    ></div>
  {/if}
</div>
